import React, { useContext } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import moment from "moment";
import { SubaccountContext } from "../../../../store/SubaccountProvider";

const InputStyle =
  "daterange-picker font-medium tracking-tight text-navy-700 h-10 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none shadow-sm border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: {
    variant: "outline",
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)",
      borderRadius: "2px",
      _hover: {
        background: "gray",
        color: "white",
      },
    },
    isInRangeBtnProps: {
      background: "gray",
      color: "white",
    },
    selectedBtnProps: {
      background: "black",
      color: "white",
    },
    todayBtnProps: {},
  },
  inputProps: {
    className: InputStyle,
  },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)",
    },
  },
  calendarPanelProps: {
    wrapperProps: {
      borderColor: "#81E6D9",
    },
    contentProps: {
      borderWidth: 0,
    },
    headerProps: {
      padding: "5px",
    },
    dividerProps: {
      display: "none",
    },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

const CommissionSearch: React.FC<{
  showFilters: boolean;
  isFilterLoading: boolean;
  filters: any;
  setFilters: any;
  selectedDates: any;
  setSelectedDates: any;
  handleSearchSubmit(): void;
  handleSearchClear(): void;
  setIsReadyToExport: any;
  setDateFiltersForExport: any;
}> = ({
  showFilters,
  isFilterLoading,
  filters,
  setFilters,
  selectedDates,
  setSelectedDates,
  handleSearchSubmit,
  handleSearchClear,
  setIsReadyToExport,
  setDateFiltersForExport,
}) => {
  const { subaccountBalance, isGettingBalance } = useContext(SubaccountContext);

  // Build asset list from subaccountBalance
  const assetList = React.useMemo(() => {
    if (!subaccountBalance || subaccountBalance.length === 0) {
      return null;
    }

    const uniqueAssets = subaccountBalance.map((item: any) => ({
      name: item.asset,
      decimal: item.decimal ?? 2,
      crypto: item.crypto
    }));

    return uniqueAssets;
  }, [subaccountBalance]);

  const handleDateChange = (dates: Date[]) => {
    setIsReadyToExport(false);
    setSelectedDates(dates);

    const data = {
      from: moment(dates[0], "DD/MM/YYYY").format("MMM D, YYYY 00:00"),
      to: moment(dates[1], "DD/MM/YYYY").format("MMM D, YYYY 23:59"),
    };
    setDateFiltersForExport(data);
  };

  const handleInputChange = async (event: any) => {
    setIsReadyToExport(false);
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  const handleClear = () => {
    handleSearchClear();
  };

  // If not showing filters, render nothing
  if (!showFilters) return null;

  return (
    <>
      <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="search-picker-custom tracking-tight text-gray-900">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Date
            </label>
            <RangeDatepicker
              disabled={isFilterLoading}
              selectedDates={selectedDates}
              onDateChange={(dates) => handleDateChange(dates)}
              propsConfigs={dateRangePropsConfigs}
              configs={{
                dateFormat: "MMM d, yyyy",
              }}
              maxDate={new Date()}
            />
          </div>
          <div className="tracking-tight text-gray-900">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Asset
            </label>
            <Select
              isDisabled={isFilterLoading}
              id="asset"
              value={filters.asset}
              onChange={handleInputChange}
              className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
            >
              <option value="">
                {isGettingBalance ? "Retrieving..." : assetList ? "All" : "Retrieving..."}
              </option>
              {assetList &&
                assetList.filter((item: any) => !item.crypto).length > 0 && (
                  <optgroup label="Fiat">
                    {assetList
                      .filter((item: any) => !item.crypto)
                      .map((asset: any) => (
                        <option key={asset.name} value={asset.name}>
                          {asset.name}
                        </option>
                      ))}
                  </optgroup>
                )}
              {assetList &&
                assetList.filter((item: any) => item.crypto).length > 0 && (
                  <optgroup label="Crypto">
                    {assetList
                      .filter((item: any) => item.crypto)
                      .map((asset: any) => (
                        <option key={asset.name} value={asset.name}>
                          {asset.name}
                        </option>
                      ))}
                  </optgroup>
                )}
            </Select>
          </div>
          <div className="tracking-tight text-gray-900">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Status
            </label>
            <Select
              isDisabled={isFilterLoading}
              id="status"
              value={filters.status}
              onChange={handleInputChange}
              className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="complete">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </Select>
          </div>
          <div className="flex items-end justify-end gap-2">
            <Button
              isDisabled={isFilterLoading}
              onClick={() => handleClear()}
              className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
            >
              Clear
            </Button>
            <Button
              isDisabled={isFilterLoading}
              onClick={() => handleSearchSubmit()}
              className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="z-9 relative tracking-tight">
                Search Commissions
              </span>
              <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
            </Button>
          </div>
        </div>
      </Box>
    </>
  );
};

export default CommissionSearch;
